import { useEffect, useState } from 'react'
import Modal from 'react-bootstrap/Modal'
import posthog from 'posthog-js'
import { useDispatch, useSelector } from 'react-redux'

import SupportChatModal from 'components/modals/supportChatModal'
import { RootState } from 'store/redux'
import { toggleSupportModal } from 'store/redux/sidebar'

interface IModalProps {
    show: boolean
    onHide: () => void
}

/**
 * Which of the three routes out of this modal the user took.
 *
 * `faq` and `telegram` were emitted until 2026-09-01 and still sit in PostHog,
 * so anything reading this event has to tolerate them — but nothing here can
 * produce them any more. Both rows led into channels this menu now reaches by
 * a shorter path: the FAQ row opened the same assistant «پشتیبانی آنلاین»
 * opens, and Telegram duplicated Eitaa, which is where support actually is.
 */
type TChannel = 'call' | 'eitaa' | 'bot'

// Ten screens render their own <SupportModal> off one redux flag, so on the
// routes where a page and its layout both do it two instances see the same
// flip. One human action, one event: whichever instance gets there first
// reports, and closing arms the next one.
let reportedOpen = false

const SupportModal = ({ show, onHide }: IModalProps) => {
    // The chatbot lives here rather than in the layout so this modal owns the
    // whole "contact support" flow: it can hand off to the bot and the bot can
    // hand back when a human is needed.
    const [showChat, setShowChat] = useState(false)
    const dispatch = useDispatch()
    const source = useSelector(
        (state: RootState) => state.sideBarControler.supportModalSource
    )
    const fromBot = source === 'bot_escalation'

    useEffect(() => {
        if (!show) {
            reportedOpen = false
            return
        }
        if (reportedOpen) return
        reportedOpen = true
        posthog.capture('support_options_opened', { source })
    }, [show, source])

    /**
     * Count which way out of this modal the user took.
     *
     * This used to also POST their name and phone number to
     * `support.gethamyan.ir/webhook/monitoring-users-messages` so support would
     * know somebody had asked for help. **That host has never resolved.** The
     * call was added 2024-02-26 and every one of them has failed since; once a
     * `.catch()` was finally added the failures showed up 1:1 with the taps.
     * In the two weeks PostHog has covered, 71 taps from 24 different people
     * went nowhere — one of them a fund owner who tapped support seven times.
     *
     * It is gone rather than repointed because it carried nothing PostHog does
     * not already have: `distinct_id` on this event IS the phone number, and
     * the name is a person property. What was missing was push, not data, and
     * push now exists where it is actually actionable — the backend messages
     * the support group when the bot escalates a question it cannot answer.
     * Adding "someone tapped تماس تلفنی" to that group would dilute the one
     * signal that means a person genuinely needs to act.
     */
    const recordChoice = (channel: TChannel) => {
        posthog.capture('support_channel_chosen', { channel })
    }

    return (
        <>
        <Modal
            show={show}
            onHide={onHide}
            size="sm"
            aria-labelledby="contained-modal-title-vcenter"
            centered
        >
            <div className=" w-full text-center py-4 text-xs">
                <p className=" mb-3 px-3">
                    از ساعت 10:30 الی 15 از طریق تماس تلفنی و از ساعت 9 تا 17 در پیام رسان‌ها، پاسخ‌گوی شما عزیزان هستیم.
                </p>
                <hr className=" mb-3" />
                {/* First, because it is the only channel that answers at the
                    moment somebody is standing in front of this modal — the
                    other two hand them off to office hours.

                    It is the assistant, under the name the landing page
                    already uses for it. There used to be two doors into the
                    same chat («سوال‌های متداول» and a trial row at the bottom
                    tagged آزمایشی); one door, no tag. `source: 'menu'` stays
                    on the event so the existing funnel keeps counting.

                    Hidden on the one route that arrives here from inside the
                    chat: the bot escalates when it cannot answer, and offering
                    it back as the top row would send a person who just asked
                    for a human straight into the thing they left. */}
                {!fromBot && (
                    <>
                        <p
                            className=" text-green-1 my-3"
                            onClick={() => {
                                recordChoice('bot')
                                onHide()
                                setShowChat(true)
                                posthog.capture('support_bot_opened', {
                                    source: 'menu',
                                })
                            }}
                        >
                            پشتیبانی آنلاین
                        </p>
                        <hr />
                    </>
                )}
                <p
                    className=" text-green-1 my-3"
                    onClick={() => {
                        recordChoice('eitaa')
                        onHide()
                        window.location.replace('https://eitaa.com/Hamyanapp')
                    }}
                >
                    پشتیبانی همیان در ایتا
                </p>
                <hr />
                {/* `inline-block` so the vertical margin actually applies — on
                    a bare inline <a> it does not, and this row sat tighter
                    than the two above it. */}
                <a
                    className=" text-green-1 my-3 no-underline inline-block"
                    onClick={() => {
                        recordChoice('call')
                        onHide()
                    }}
                    href="tel:02191070174"
                >
                    تماس تلفنی
                </a>
                <hr />
                <p className=" mt-4" onClick={onHide}>
                    انصراف
                </p>
            </div>
        </Modal>

        <SupportChatModal
            show={showChat}
            onHide={() => setShowChat(false)}
            onWantsHuman={() => {
                setShowChat(false)
                dispatch(
                    toggleSupportModal({
                        isOpenSupportModal: true,
                        source: 'bot_escalation',
                    })
                )
            }}
        />
        </>
    )
}

export default SupportModal
